import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery, useStatus } from '@powersync/react';
import { useApp, isCloud } from '@/app/state';
import { setLang, type Lang } from '@/i18n';
import { cx } from './ui';
import { Logo } from './Logo';
import { getDeviceMode } from '@/app/device';
import { applyPendingUpdate, onUpdateReady } from '@/app/updates';
import { Link } from 'react-router';

const NAV: { to: string; key: string; roles: string[] }[] = [
  { to: '/reception', key: 'nav.reception', roles: ['owner', 'receptionist', 'nurse'] },
  { to: '/queue', key: 'nav.queue', roles: ['owner', 'doctor', 'nurse', 'receptionist'] },
  { to: '/dispense', key: 'nav.dispense', roles: ['owner', 'pharmacist', 'nurse'] },
  { to: '/stock', key: 'nav.stock', roles: ['owner', 'pharmacist'] },
  { to: '/patients', key: 'nav.patients', roles: ['owner', 'doctor', 'nurse', 'receptionist', 'cashier', 'pharmacist'] },
  { to: '/settings', key: 'nav.settings', roles: ['owner'] },
];

export function Shell() {
  const { t, i18n } = useTranslation();
  const { session, clinic, setSession } = useApp();
  const status = useStatus();
  const lang = i18n.language as Lang;
  const { data: crud } = useQuery<{ n: number }>('select count(*) as n from ps_crud');
  const cloud = isCloud && session?.mode === 'cloud';
  const pending = cloud ? (crud?.[0]?.n ?? 0) : 0;
  const online = cloud ? status.connected : true;
  const mode = getDeviceMode() ?? 'full';
  const single = mode !== 'full';
  const visibleNav = NAV.filter((n) => n.roles.some((r) => session?.roles.includes(r as never)));
  const [updateReady, setUpdateReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => onUpdateReady(setUpdateReady), []);

  return (
    <div className="min-h-dvh flex flex-col">
      <header className="no-print sticky top-0 z-30 border-b border-rule bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2">
          <div className="flex items-center gap-2 font-bold text-accent-ink">
            <Logo className="h-7 w-7 text-accent" />
            <span className={lang === 'my' ? 'font-my' : ''}>{(lang === 'my' && clinic?.name_my) || clinic?.name || t('app.name')}</span>
          </div>
          {single && <span className="ml-2 rounded-full bg-accent-soft px-2.5 py-0.5 text-xs font-semibold text-accent-ink">{t(`device.${mode}`)}</span>}
          <nav className={cx('ml-4 gap-1', single ? 'hidden' : 'hidden sm:flex')}>
            {visibleNav.map((n) => (
              <NavLink key={n.to} to={n.to} className={({ isActive }) => cx('whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-semibold', isActive ? 'bg-accent-soft text-accent-ink' : 'text-ink-2 hover:bg-bg')}>{t(n.key)}</NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2 text-sm">
            <span className={cx('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold', online ? 'bg-accent-soft text-accent-ink' : 'bg-warn-soft text-warn')} title={pending ? t('common.otherDevicesStale') : undefined}>
              <span className={cx('h-2 w-2 rounded-full', online ? 'bg-accent' : 'bg-warn')} />
              {cloud ? (online ? t('common.online') : t('common.offline')) : 'Demo'}
              {pending > 0 && <span>· {t('common.pendingChanges', { count: pending })}</span>}
            </span>
            <button className="rounded-lg border border-rule px-2 py-1 text-xs font-semibold" onClick={() => setLang(lang === 'my' ? 'en' : 'my')} aria-label={t('common.language')}>{lang === 'my' ? 'EN' : 'မြန်မာ'}</button>
            {single && session?.roles.includes('owner') && <Link to="/settings" className="rounded-lg px-2 py-1 text-xs text-ink-2 hover:bg-bg" aria-label={t('nav.settings')}>⚙︎</Link>}
            <button className="rounded-lg px-2 py-1 text-xs text-ink-2 hover:bg-bg" onClick={() => setSession(null)}>{t('common.signOut')}</button>
          </div>
        </div>
        <nav className={cx('gap-1 overflow-x-auto px-2 pb-2', single ? 'hidden' : 'flex sm:hidden')}>
          {visibleNav.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => cx('whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-semibold', isActive ? 'bg-accent-soft text-accent-ink' : 'text-ink-2')}>{t(n.key)}</NavLink>
          ))}
        </nav>
      </header>
      {updateReady && !dismissed && (
        <div className="no-print flex flex-wrap items-center justify-center gap-3 bg-accent px-4 py-2 text-sm font-semibold text-white">
          <span>{t('update.available')}</span>
          <button className="rounded-lg bg-white px-3 py-1 font-bold text-accent-ink" onClick={applyPendingUpdate}>{t('update.reload')}</button>
          <button className="underline opacity-80" onClick={() => setDismissed(true)}>{t('update.later')}</button>
        </div>
      )}
      <main className={cx('mx-auto w-full max-w-6xl flex-1 px-4 py-5', lang === 'my' && 'font-my')}>
        <Outlet />
      </main>
    </div>
  );
}
